import { getCompanyList } from "../services/companyApi";
import { getUserList } from "../services/userApi";
import { getAllProjects } from "../services/projectApi";
import { listProjectInvitations } from "../services/invitationApi";
import { getFrameworks } from "../services/frameworkApi";
import { GetAllCapabilities } from "../services/capabilitiesApi";
import { getTraxccelFrameworks } from "../services/traxccelFrameworkApi";
import { getAssessmentBlueprints } from "../services/assessmentBlueprintApi";
import { SA_DASHBOARD_CACHE_KEY } from "./saDashboardCache";

export interface RoleCount {
  roleId: number;
  roleName: string;
  count: number;
}

export interface ProjectOverviewRow {
  projectId: number;
  projectName: string;
  companyId: number | null;
  companyName: string;
  memberTotal: number;
  clientCount: number;
  superUserCount: number;
  smeHint: number;
}

export interface FrameworkCatalogCounts {
  sourceFrameworks: number;
  capabilities: number;
  catalogRows: number;
  traxccelFrameworks: number;
  assessmentBlueprints: number;
}

export type AttentionItemKind =
  | "capability_approval"
  | "project_setup_approval"
  | "company_no_projects"
  | "project_no_members"
  | "project_pending_invites";

export interface AttentionItem {
  id: string;
  kind: AttentionItemKind;
  title: string;
  meta: string;
  path: string;
  priority: number;
}

export interface SuperAdminDashboardAttentionScanMeta {
  companiesWithoutProjects: Array<{ companyId: number; companyName: string }>;
  projectsNoMembers: Array<{
    projectId: number;
    projectName: string;
    companyId: number | null;
  }>;
  projectsPendingInvites: Array<{
    projectId: number;
    projectName: string;
    companyId: number | null;
    pendingCount: number;
  }>;
}

export interface SuperAdminDashboardData {
  companies: number;
  projects: number;
  totalUsers: number;
  roleCounts: RoleCount[];
  topProjects: ProjectOverviewRow[];
  pendingApprovals: { capability: number; projectSetup: number };
  frameworkCatalog: FrameworkCatalogCounts;
  attentionItems: AttentionItem[];
  attentionScanMeta: SuperAdminDashboardAttentionScanMeta;
}

const SA_DASHBOARD_CACHE_TTL_MS = 90 * 1000;

const ATTENTION_SCAN_LIMIT = 40;
const MAX_ATTENTION_ITEMS = 5;

const countInvitationsByRole = (
  invitations: Awaited<ReturnType<typeof listProjectInvitations>>
) => {
  let clientCount = 0;
  let superUserCount = 0;
  let activeTotal = 0;
  let pendingTotal = 0;

  for (const invite of invitations) {
    const status = String(invite.status || "").toLowerCase();
    if (status === "pending") {
      pendingTotal += 1;
      continue;
    }
    if (status !== "consumed") continue;

    activeTotal += 1;
    const role = String(invite.roleName || "").toLowerCase();
    if (role === "client") clientCount += 1;
    if (role === "superuser") superUserCount += 1;
  }

  return { activeTotal, clientCount, superUserCount, pendingTotal };
};

export const buildSuperAdminAttentionItems = (input: {
  pendingCapabilityCount: number;
  pendingProjectSetupCount: number;
  companiesWithoutProjects: Array<{ companyId: number; companyName: string }>;
  projectsNoMembers: Array<{
    projectId: number;
    projectName: string;
    companyId: number | null;
  }>;
  projectsPendingInvites: Array<{
    projectId: number;
    projectName: string;
    companyId: number | null;
    pendingCount: number;
  }>;
}): AttentionItem[] => {
  const items: AttentionItem[] = [];

  if (input.pendingCapabilityCount > 0) {
    items.push({
      id: "capability-approvals",
      kind: "capability_approval",
      title: `${input.pendingCapabilityCount} capability request${
        input.pendingCapabilityCount !== 1 ? "s" : ""
      } awaiting review`,
      meta: "Review and approve capability catalog changes",
      path: "/sa/capability_approval",
      priority: 1,
    });
  }

  if (input.pendingProjectSetupCount > 0) {
    items.push({
      id: "project-setup-approvals",
      kind: "project_setup_approval",
      title: `${input.pendingProjectSetupCount} project setup request${
        input.pendingProjectSetupCount !== 1 ? "s" : ""
      } awaiting review`,
      meta: "Approve new client project configurations",
      path: "/sa/project_setup_approval",
      priority: 2,
    });
  }

  for (const company of input.companiesWithoutProjects.slice(0, 2)) {
    items.push({
      id: `company-${company.companyId}`,
      kind: "company_no_projects",
      title: company.companyName,
      meta: "Company has no projects yet",
      path: "/sa/company_management",
      priority: 3,
    });
  }

  for (const project of input.projectsNoMembers.slice(0, 2)) {
    items.push({
      id: `project-empty-${project.projectId}`,
      kind: "project_no_members",
      title: project.projectName,
      meta: "Project has no active team members",
      path: projectManagementLink(project.companyId),
      priority: 4,
    });
  }

  for (const project of input.projectsPendingInvites.slice(0, 2)) {
    items.push({
      id: `project-pending-${project.projectId}`,
      kind: "project_pending_invites",
      title: project.projectName,
      meta: `${project.pendingCount} pending invitation${
        project.pendingCount !== 1 ? "s" : ""
      } not accepted`,
      path: projectManagementLink(project.companyId),
      priority: 5,
    });
  }

  return items
    .sort((a, b) => a.priority - b.priority)
    .slice(0, MAX_ATTENTION_ITEMS);
};

function readDashboardCache():
  | { loadedAt: number; data: SuperAdminDashboardData }
  | null {
  try {
    const raw = sessionStorage.getItem(SA_DASHBOARD_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      loadedAt: number;
      data: SuperAdminDashboardData;
    };
    if (!parsed?.data || !parsed.loadedAt) return null;
    if (Date.now() - parsed.loadedAt > SA_DASHBOARD_CACHE_TTL_MS) {
      sessionStorage.removeItem(SA_DASHBOARD_CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeDashboardCache(data: SuperAdminDashboardData): void {
  try {
    sessionStorage.setItem(
      SA_DASHBOARD_CACHE_KEY,
      JSON.stringify({ loadedAt: Date.now(), data })
    );
  } catch {
    // ignore quota / private mode
  }
}

export function mergeSuperAdminDashboardPendingCounts(
  data: SuperAdminDashboardData,
  pendingCapabilityCount: number,
  pendingProjectSetupCount: number
): SuperAdminDashboardData {
  const attentionItems = buildSuperAdminAttentionItems({
    pendingCapabilityCount,
    pendingProjectSetupCount,
    companiesWithoutProjects: data.attentionScanMeta.companiesWithoutProjects,
    projectsNoMembers: data.attentionScanMeta.projectsNoMembers,
    projectsPendingInvites: data.attentionScanMeta.projectsPendingInvites,
  });

  return {
    ...data,
    pendingApprovals: {
      capability: pendingCapabilityCount,
      projectSetup: pendingProjectSetupCount,
    },
    attentionItems,
  };
}

export async function loadSuperAdminDashboardData(
  pendingCapabilityCount = 0,
  pendingProjectSetupCount = 0
): Promise<SuperAdminDashboardData> {
  const cached = readDashboardCache();
  if (cached) {
    return mergeSuperAdminDashboardPendingCounts(
      cached.data,
      pendingCapabilityCount,
      pendingProjectSetupCount
    );
  }

  const [
    companiesResult,
    usersResult,
    projectsResult,
    frameworksResult,
    capabilitiesMetaResult,
    traxccelResult,
    blueprintResult,
    companyListResult,
  ] = await Promise.allSettled([
    getCompanyList(1, 1),
    getUserList(1, 1),
    getAllProjects(),
    getFrameworks(1, 1),
    GetAllCapabilities(1, 1),
    getTraxccelFrameworks(1, 1),
    getAssessmentBlueprints(1, 1),
    getCompanyList(1, 100),
  ]);

  const companies =
    companiesResult.status === "fulfilled"
      ? companiesResult.value?.totalCount ?? 0
      : 0;

  const totalUsers =
    usersResult.status === "fulfilled"
      ? usersResult.value?.totalCount ?? 0
      : 0;

  const projectsRaw =
    projectsResult.status === "fulfilled" && Array.isArray(projectsResult.value)
      ? projectsResult.value
      : [];

  const normalizedProjects = projectsRaw.map((p) => ({
    projectId: p.projectId ?? p.id ?? p.ProjectId ?? 0,
    projectName: p.projectName ?? p.ProjectName ?? "Unnamed project",
    companyId: p.companyId ?? p.CompanyId ?? null,
    companyName: p.companyName ?? p.CompanyName ?? "—",
  }));

  const capabilityTotal =
    capabilitiesMetaResult.status === "fulfilled"
      ? capabilitiesMetaResult.value?.totalCount ?? 0
      : 0;

  const frameworkCatalog: FrameworkCatalogCounts = {
    sourceFrameworks:
      frameworksResult.status === "fulfilled"
        ? frameworksResult.value?.totalCount ?? 0
        : 0,
    capabilities: capabilityTotal,
    catalogRows: capabilityTotal,
    traxccelFrameworks:
      traxccelResult.status === "fulfilled"
        ? traxccelResult.value?.totalCount ?? 0
        : 0,
    assessmentBlueprints:
      blueprintResult.status === "fulfilled"
        ? blueprintResult.value?.totalCount ?? 0
        : 0,
  };

  const companyIdsWithProjects = new Set(
    normalizedProjects
      .map((project) => project.companyId)
      .filter((id): id is number => id != null && id > 0)
  );

  const companiesWithoutProjects =
    companyListResult.status === "fulfilled"
      ? (companyListResult.value?.items ?? [])
          .map((company) => ({
            companyId:
              company.companyId ??
              company.id ??
              company.CompanyId ??
              company.ID ??
              0,
            companyName:
              company.companyName ??
              company.name ??
              company.CompanyName ??
              "Unnamed company",
          }))
          .filter(
            (company) =>
              company.companyId > 0 &&
              !companyIdsWithProjects.has(company.companyId)
          )
      : [];

  const sampleForScan = [...normalizedProjects]
    .sort((a, b) => b.projectId - a.projectId)
    .slice(0, ATTENTION_SCAN_LIMIT);

  const invitationResults = await Promise.allSettled(
    sampleForScan.map(async (project) => {
      if (!project.projectId) {
        return {
          project,
          activeTotal: 0,
          clientCount: 0,
          superUserCount: 0,
          pendingTotal: 0,
        };
      }
      try {
        const invitations = await listProjectInvitations(project.projectId);
        const counts = countInvitationsByRole(invitations);
        return { project, ...counts };
      } catch {
        return {
          project,
          activeTotal: 0,
          clientCount: 0,
          superUserCount: 0,
          pendingTotal: 0,
        };
      }
    })
  );

  const invitationScan = invitationResults
    .filter(
      (r): r is PromiseFulfilledResult<{
        project: (typeof sampleForScan)[0];
        activeTotal: number;
        clientCount: number;
        superUserCount: number;
        pendingTotal: number;
      }> => r.status === "fulfilled"
    )
    .map((r) => r.value);

  const ranked = [...invitationScan]
    .sort((a, b) => {
      if (b.activeTotal !== a.activeTotal) return b.activeTotal - a.activeTotal;
      return b.project.projectId - a.project.projectId;
    })
    .slice(0, 5)
    .map(
      ({ project, activeTotal, clientCount, superUserCount }): ProjectOverviewRow => ({
        projectId: project.projectId,
        projectName: project.projectName,
        companyId: project.companyId,
        companyName: project.companyName,
        memberTotal: activeTotal,
        clientCount,
        superUserCount,
        smeHint: 0,
      })
    );

  const attentionScan = invitationScan;

  const projectsNoMembers = attentionScan
    .filter((entry) => entry.activeTotal === 0)
    .map(({ project }) => ({
      projectId: project.projectId,
      projectName: project.projectName,
      companyId: project.companyId,
    }));

  const projectsPendingInvites = attentionScan
    .filter((entry) => entry.pendingTotal > 0)
    .sort((a, b) => b.pendingTotal - a.pendingTotal)
    .map(({ project, pendingTotal }) => ({
      projectId: project.projectId,
      projectName: project.projectName,
      companyId: project.companyId,
      pendingCount: pendingTotal,
    }));

  const attentionScanMeta: SuperAdminDashboardAttentionScanMeta = {
    companiesWithoutProjects,
    projectsNoMembers,
    projectsPendingInvites,
  };

  const payload: SuperAdminDashboardData = {
    companies,
    projects: normalizedProjects.length,
    totalUsers,
    roleCounts: [],
    topProjects: ranked,
    pendingApprovals: {
      capability: pendingCapabilityCount,
      projectSetup: pendingProjectSetupCount,
    },
    frameworkCatalog,
    attentionItems: buildSuperAdminAttentionItems({
      pendingCapabilityCount,
      pendingProjectSetupCount,
      ...attentionScanMeta,
    }),
    attentionScanMeta,
  };

  writeDashboardCache(payload);
  return payload;
}

export function userManagementLink(roleId?: number | string | null): string {
  if (roleId == null || roleId === "" || roleId === 0) {
    return "/sa/user_management";
  }
  return `/sa/user_management?roleId=${encodeURIComponent(String(roleId))}`;
}

export function projectManagementLink(companyId?: number | null): string {
  if (!companyId) return "/sa/project_management";
  return `/sa/project_management?companyId=${encodeURIComponent(String(companyId))}`;
}
